import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { shortnameToUnicode } from "emoji-toolkit";
import Linkify from "react-linkify";
import truncate from "lodash/truncate";
import { Button } from "reactstrap";

import Toast from "shared/toast";

import DeleteCommentConfirmationModal from './DeleteCommentConfirmationModal';
import CommentActionMenu from './CommentActionMenu';
import CommentEditedLabel from './CommentEditedLabel';
import CommentTimestamp from './CommentTimestamp';
import {
  commentHeader,
  commentText,
  commentTimestamp,
  commentUsername,
  commentUsernameDeleted,
  textContainer,
  commentContainer,
  commentContainerHighlighted
} from "./styles/Comments.module.scss";

const COLLAPSE_LONG_TEXT_MIN_CHARS = 1000;
const COLLAPSE_TALL_TEXT_MIN_NEWLINES = 6;
const NEWLINES_REGEX = /\r\n|\r|\n/;

const isTextLong = text => text.length > COLLAPSE_LONG_TEXT_MIN_CHARS;
const isTextTall = text => text.split(NEWLINES_REGEX).length > COLLAPSE_TALL_TEXT_MIN_NEWLINES;
const isTextExpandable = text => isTextLong(text) || isTextTall(text);

const truncateText = text => {
  return isTextLong(text)
    ? truncate(text, { length: COLLAPSE_LONG_TEXT_MIN_CHARS, separator: /\s/ })
    : text.split(NEWLINES_REGEX).slice(0, COLLAPSE_TALL_TEXT_MIN_NEWLINES).join("\n").concat("...");
};

const CommentDisplay = ({
  createdAt,
  editable,
  hashid,
  threadHashid,
  lastEditedAt,
  following,
  onFollowingToggle,
  onDelete,
  onEdit,
  richText,
  createdByName,
  createdByDeleted,
  highlight
}) => {
  const commentRef = useRef();

  const [scrolledToHighlight, setScrolledToHighlight] = useState(false);
  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [forceActionMenuVisible, setForceActionMenuVisible] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const textWithEmojis = shortnameToUnicode(richText);
  const [textExpanded, setTextExpanded] = useState(!isTextExpandable(textWithEmojis));

  useEffect(() => {
    if (highlight && !scrolledToHighlight && commentRef.current) {
      commentRef.current.scrollIntoView({ block: "center" });
      setScrolledToHighlight(true);
    }
  });

  const onShare = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("thread_id", threadHashid);
    url.searchParams.set("comment_id", hashid);
    window.navigator.clipboard.writeText(url);
    Toast.success({ message: "Link copied to clipboard", timeOut: 1500 });
  };

  const onKebabToggled = kebabOpen => setForceActionMenuVisible(kebabOpen);
  const toggleModal = () => setModalIsOpen(!modalIsOpen);

  const formattedUsername = ({ createdByName, createdByDeleted }) => {
    if (createdByDeleted) {
      return createdByName ? `${createdByName} (Deleted)` : "(Deleted)";
    } else {
      return createdByName;
    }
  };

  return (
    <div>
      <DeleteCommentConfirmationModal
        hashid={hashid}
        onDelete={onDelete}
        richText={textWithEmojis}
        isOpen={modalIsOpen}
        toggle={toggleModal}
      />
      <div
        ref={commentRef}
        className={highlight ? commentContainerHighlighted : commentContainer}
        onMouseEnter={() => setActionMenuVisible(true)}
        onMouseLeave={() => setActionMenuVisible(false)}
        data-test-id="comment-container"
      >
        <div className={commentHeader}>
          <div className="d-flex align-items-center">
            <div data-test-id="username" className={createdByDeleted ? commentUsernameDeleted : commentUsername}>
              {formattedUsername({ createdByName, createdByDeleted })}
            </div>
            <CommentTimestamp className={commentTimestamp} timestamp={createdAt} tooltip />
          </div>
          <div
            style={{ visibility: actionMenuVisible || forceActionMenuVisible ? "visible" : "hidden" }}
            data-test-id="action-menu-container"
          >
            <CommentActionMenu
              editable={editable}
              following={following}
              onFollowingToggle={onFollowingToggle}
              onEdit={onEdit}
              onDelete={toggleModal}
              onShare={onShare}
              onKebabToggled={onKebabToggled}
            />
          </div>
        </div>
        <div className={textContainer}>
          <Linkify properties={{ target: "_blank" }}>
            <div className={commentText}>
              {textExpanded
                ? textWithEmojis
                : truncateText(textWithEmojis)
              }
              {isTextExpandable(textWithEmojis) &&
                <Button
                  className="p-0 ml-2"
                  onClick={() => setTextExpanded(!textExpanded)}
                  color="link"
                  data-test-id="expand-collapse-text-button"
                >
                  {textExpanded ? "Collapse" : "Expand"}
                </Button>
              }
            </div>

          </Linkify>

          {lastEditedAt &&
            <div className="mt-1">
              <CommentEditedLabel lastEditedAt={lastEditedAt} />
            </div>
          }
        </div>
      </div>
    </div>
  );
};

CommentDisplay.propTypes = {
  threadHashid: PropTypes.string,
  createdAt: PropTypes.string,
  editable: PropTypes.bool,
  hashid: PropTypes.string.isRequired,
  lastEditedAt: PropTypes.string,
  following: PropTypes.bool,
  onFollowingToggle: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  richText: PropTypes.string.isRequired,
  createdByName: PropTypes.string,
  createdByDeleted: PropTypes.bool,
  highlight: PropTypes.bool,
  initScrollToHighlight: PropTypes.bool
};

CommentDisplay.defaultProps = {
  highlight: false
};

export default CommentDisplay;
